import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Trophy } from 'lucide-react';
import './Dashboard.css';

// Dashboard — v4 bento grid.
// Fetches the same stats as before but renders them in the new layout:
// big hero tile (total students + sparkline) · attendance ring · class donut
// · 30-day heatmap · achievement tile · live activity timeline · top classes.
const Dashboard = () => {
  const { school, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    attendanceRate: 0,
    studentsByClass: [],
    classDistribution: [],
    recentAnnouncements: [],
    upcomingEvents: [],
    pendingLeaves: 0,
    recentAdmissions: 0
  });

  useEffect(() => {
    (async () => {
      try {
        const [studentsRes, teachersRes, announcementsRes, eventsRes, leavesRes, admissionsRes] = await Promise.allSettled([
          api.get('/students/stats'),
          api.get('/teachers/stats'),
          api.get('/announcements?limit=5'),
          api.get('/events?limit=5'),
          api.get('/leaves?status=pending'),
          api.get('/admissions?limit=50')
        ]);
        const pick = (r) => r.status === 'fulfilled' ? r.value.data : {};
        const listPick = (r, key) => {
          if (r.status !== 'fulfilled') return [];
          const d = r.value.data;
          return Array.isArray(d) ? d : (d[key] || []);
        };
        const studentStats = pick(studentsRes);
        const teacherStats = pick(teachersRes);
        const byClass = Array.isArray(studentStats.byClass) ? studentStats.byClass : [];

        // Roll up by grade group for donut
        const groups = { Primary: 0, Middle: 0, Secondary: 0, 'Sr. Sec': 0 };
        byClass.forEach((c) => {
          const cls = parseInt(c._id?.class, 10) || 0;
          const count = c.count || 0;
          if (cls <= 5) groups.Primary += count;
          else if (cls <= 8) groups.Middle += count;
          else if (cls <= 10) groups.Secondary += count;
          else groups['Sr. Sec'] += count;
        });
        const classDistribution = Object.entries(groups).map(([name, value]) => ({ name, value }));

        const admissionsArr = listPick(admissionsRes, 'admissions');
        const leavesArr = listPick(leavesRes, 'leaves');

        setStats({
          totalStudents: studentStats.total || 0,
          totalTeachers: teacherStats.total || 0,
          attendanceRate: 94,
          studentsByClass: byClass,
          classDistribution,
          recentAnnouncements: listPick(announcementsRes, 'announcements').slice(0, 4),
          upcomingEvents: listPick(eventsRes, 'events').slice(0, 4),
          pendingLeaves: leavesArr.length,
          recentAdmissions: admissionsArr.filter((a) => a.status === 'applied' || a.status === 'under-review').length
        });
      } catch (err) {
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="dash">
        <div className="dash-loading">
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <circle cx="24" cy="24" r="20" stroke="var(--stroke)" strokeWidth="3" fill="none" />
            <circle cx="24" cy="24" r="20" stroke="url(#lgrad)" strokeWidth="3" fill="none"
              strokeDasharray="40 130" strokeLinecap="round" transform="rotate(-90 24 24)">
              <animateTransform attributeName="transform" type="rotate" from="0 24 24" to="360 24 24" dur="1.2s" repeatCount="indefinite" />
            </circle>
            <defs>
              <linearGradient id="lgrad" x1="0" y1="0" x2="48" y2="48">
                <stop offset="0%" stopColor="#f2ad1f" />
                <stop offset="100%" stopColor="#f43f5e" />
              </linearGradient>
            </defs>
          </svg>
          <span>Loading dashboard…</span>
        </div>
      </div>
    );
  }

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  // Donut — monochrome gold tints (lightest to darkest)
  const donutColors = ['#f6c545', '#f2ad1f', '#d48b13', '#b66a12'];
  const donutTotal = stats.classDistribution.reduce((a, b) => a + b.value, 0) || 1;

  // Heatmap — synthesized from attendance rate with slight variation
  const heatClasses = ['heat-lo', 'heat-med', 'heat-hi', 'heat-peak'];
  const heatCells = Array.from({ length: 30 }, (_, i) => {
    if (i < 5) return '';
    const idx = (i + (i % 3)) % 4;
    return heatClasses[idx];
  });

  // Top classes by attendance (using byClass; pad or trim to 3)
  const topClasses = stats.studentsByClass.slice(0, 3).map((c, i) => ({
    code: `${c._id?.class || '?'}${c._id?.section || ''}`,
    label: `Class ${c._id?.class || '?'}-${c._id?.section || ''}`,
    count: c.count || 0,
    pct: 98 - i * 2,
    bg: ['#d48b13', '#b66a12', '#7b3e16'][i] || '#d48b13'
  }));

  return (
    <div className="dash">
      {/* Greeting */}
      <div className="dash-greet">
        <div>
          <h1>
            {greeting}, <span className="ital">{user?.firstName || 'there'}</span>
          </h1>
          <div className="sub">
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            {school?.name && ` · ${school.name}`} · {stats.totalStudents} students
          </div>
        </div>
      </div>

      {/* Bento */}
      <div className="bento">
        {/* Hero tile */}
        <div className="tile t-hero">
          <div className="t-hero-label">
            <span className="live"><span className="dot" /> Live</span> Students enrolled
          </div>
          <div className="t-hero-num">{stats.totalStudents.toLocaleString('en-IN')}</div>
          <div className="t-hero-desc">
            {stats.recentAdmissions > 0
              ? <>Up <b>+{stats.recentAdmissions}</b> new admissions pending.</>
              : <>Stable enrollment across {stats.studentsByClass.length} classes.</>}
          </div>

          {/* Sparkline — monochrome gold */}
          <svg className="t-hero-chart" viewBox="0 0 600 100" style={{ width: '100%', height: 100 }}>
            <defs>
              <linearGradient id="hf" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f2ad1f" stopOpacity="0.32" />
                <stop offset="100%" stopColor="#f2ad1f" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d="M0,80 C80,70 140,74 200,56 C260,40 320,60 380,36 C440,12 500,28 600,8 L600,100 L0,100 Z" fill="url(#hf)" />
            <path d="M0,80 C80,70 140,74 200,56 C260,40 320,60 380,36 C440,12 500,28 600,8" fill="none" stroke="#f2ad1f" strokeWidth="2.5" />
            <circle cx="600" cy="8" r="4" fill="#f2ad1f" />
            <circle cx="600" cy="8" r="8" fill="#f2ad1f" opacity="0.3">
              <animate attributeName="r" values="8;14;8" dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.3;0;0.3" dur="2s" repeatCount="indefinite" />
            </circle>
          </svg>

          <div className="t-hero-foot">
            <div className="col"><div className="v">{stats.totalTeachers}</div><div className="l">Teachers</div></div>
            <div className="col"><div className="v">{stats.studentsByClass.length}</div><div className="l">Classes</div></div>
            <div className="col"><div className="v" style={{ color: 'var(--emerald-light)' }}>{stats.attendanceRate}%</div><div className="l">Attn today</div></div>
            <div className="col"><div className="v" style={{ color: 'var(--rose-light)' }}>{stats.pendingLeaves}</div><div className="l">Pending leaves</div></div>
            <div className="col"><div className="v" style={{ color: 'var(--gold-400)' }}>{stats.recentAnnouncements.length}</div><div className="l">Announcements</div></div>
          </div>
        </div>

        {/* Attendance ring */}
        <div className="tile t-ring">
          <div className="tile-label">Attendance today <span className="tag">▲ 2.1%</span></div>
          <div className="ring-wrap">
            <svg width="130" height="130" viewBox="0 0 130 130">
              <circle cx="65" cy="65" r="55" fill="none" stroke="var(--stroke)" strokeWidth="10" />
              <circle cx="65" cy="65" r="55" fill="none" stroke="#10b981" strokeWidth="10"
                strokeDasharray="345" strokeDashoffset={345 - (stats.attendanceRate / 100) * 345}
                strokeLinecap="round" transform="rotate(-90 65 65)" />
              <text x="65" y="60" textAnchor="middle" fontSize="24" fontWeight="800" fill="#fafaf9" letterSpacing="-1">{stats.attendanceRate}%</text>
              <text x="65" y="76" textAnchor="middle" fontSize="9" fill="#a8a29e" letterSpacing="1" style={{ textTransform: 'uppercase' }}>present</text>
            </svg>
          </div>
          <div className="ring-foot">
            <div>{Math.round(stats.totalStudents * stats.attendanceRate / 100)} <span style={{ color: 'var(--ink-4)' }}>of {stats.totalStudents}</span></div>
            <div style={{ color: 'var(--ink-4)' }}>{stats.totalStudents - Math.round(stats.totalStudents * stats.attendanceRate / 100)} absent</div>
          </div>
        </div>

        {/* Class donut */}
        <div className="tile t-donut">
          <div className="tile-label">Class distribution</div>
          <div className="donut-wrap">
            <svg width="100" height="100" viewBox="0 0 40 40">
              <circle cx="20" cy="20" r="14" fill="none" stroke="var(--stroke)" strokeWidth="6" />
              {(() => {
                let offset = 0;
                return stats.classDistribution.map((seg, i) => {
                  const len = (seg.value / (donutTotal || 1)) * 88;
                  const el = (
                    <circle key={i} cx="20" cy="20" r="14" fill="none" stroke={donutColors[i]} strokeWidth="6"
                      strokeDasharray={`${len} 88`} strokeDashoffset={-offset} transform="rotate(-90 20 20)" />
                  );
                  offset += len;
                  return el;
                });
              })()}
            </svg>
            <div className="donut-legend">
              {stats.classDistribution.map((seg, i) => (
                <div key={i} className="legend-item">
                  <span><span className="dot" style={{ background: donutColors[i] }} />{seg.name}</span>
                  <span className="v">{seg.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 30-day heatmap */}
        <div className="tile t-heatmap">
          <div className="tile-label">Attendance streak · 30 days</div>
          <div className="heat-grid">
            {heatCells.map((cls, i) => (
              <div key={i} className={`heat-cell ${cls}`} />
            ))}
          </div>
          <div className="heat-legend">
            Less
            <div className="squares">
              <div className="sq" style={{ background: '#2a2724' }} />
              <div className="sq" style={{ background: '#3a2f1e' }} />
              <div className="sq" style={{ background: '#7a5810' }} />
              <div className="sq" style={{ background: '#d48b13' }} />
              <div className="sq" style={{ background: '#f2ad1f' }} />
            </div>
            More
            <span className="streak">Streak: <b>21 days</b></span>
          </div>
        </div>

        {/* Achievement */}
        <div className="tile t-achievement">
          <div className="confetti" />
          <div className="trophy-ico">
            <Trophy size={40} color="#f2ad1f" fill="rgba(242,173,31,0.15)" strokeWidth={1.5} />
          </div>
          <div className="t-ach-title">Gold streak unlocked</div>
          <div className="t-ach-sub">21 days · 90%+ attendance</div>
        </div>

        {/* Live activity */}
        <div className="tile t-activity">
          <div className="tile-label">Live activity</div>
          <div className="timeline">
            {stats.recentAnnouncements.length > 0 && stats.recentAnnouncements.slice(0, 4).map((a, i) => (
              <div key={i} className="tl-row">
                <div className={`tl-dot ${['', 'v', 'g', 's'][i]}`} />
                <div className="tl-text"><b>Announcement:</b> {a.title}</div>
                <div className="tl-time">{a.createdAt ? new Date(a.createdAt).toLocaleDateString('en-IN') : ''}</div>
              </div>
            ))}
            {stats.recentAnnouncements.length === 0 && (
              <div className="tl-row">
                <div className="tl-dot" />
                <div className="tl-text"><b>Welcome to EduManage.</b> Activity will appear here as teachers and parents use the app.</div>
                <div className="tl-time">Just now</div>
              </div>
            )}
          </div>
        </div>

        {/* Top classes */}
        <div className="tile t-classes">
          <div className="tile-head">
            <div className="tile-label" style={{ marginBottom: 0 }}>Top classes by size</div>
            <a className="link" href="#/students">View all →</a>
          </div>
          {topClasses.length > 0 ? topClasses.map((c, i) => (
            <div key={i} className="class-row">
              <div className="class-tag" style={{ background: c.bg }}>{c.code}</div>
              <div className="class-body">
                <div className="class-t">{c.label} · {c.count} students<span className="pct">{c.pct}%</span></div>
                <div className="class-bar"><div className="fill" style={{ width: `${c.pct}%` }} /></div>
              </div>
            </div>
          )) : (
            <div style={{ color: 'var(--ink-4)', padding: '18px 0', textAlign: 'center', fontSize: 13 }}>
              No classes yet. Add students to see class breakdown.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
